import {
  createContext,
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

import {
  BranchOfficeDto,
  BranchOfficeService,
  CenterDto,
  CenterRequestDto,
  CenterService,
  WorkshopDto,
  WorkshopService,
} from '@/_generatedApi';
import SettingsCenterForm, {
  SettingsCenterFormOutputValues,
} from '@/components/settings-centers/SettingsCenterForm';
import CDataGrid, { ColDef } from '@/components/ui/common/data-grid/CDataGrid';
import Modal from '@/components/ui/common/modal/CModal';
import { Option } from '@/components/ui/fields/types';
import CentersFilters, {
  CenterFilter,
} from '@/components/ui/filters/CentersFilters';
import { ITEMS_PER_PAGE, MAX_TAKE_ITEMS } from '@/constants/pagination';
import { useShowToast } from '@/hooks/use-show-toast';
import { updateFilterData, updateObjectInArray } from '@/utils/immutable';
import { getSkipCount } from '@/utils/request';
import { nullFieldsToUndefined } from '@/utils/validation';

const fetchWorkShops = async () => {
  return (
    await WorkshopService.getWorkshopList({
      sortBy: 'name',
      descending: false,
      skip: 0,
      take: MAX_TAKE_ITEMS,
    })
  ).data as WorkshopDto[];
};

const fetchWorkBranchOffices = async () => {
  return (
    await BranchOfficeService.getBranchOfficeList({
      sortBy: 'name',
      descending: false,
      skip: 0,
      take: MAX_TAKE_ITEMS,
    })
  ).data as BranchOfficeDto[];
};

const fetchCenters = async (
  page: number,
  itemsPerPage: number,
  filter: CenterFilter
) => {
  const skip = getSkipCount(page, itemsPerPage);

  return await CenterService.getCenterList({
    sortBy: 'name',
    descending: false,
    skip,
    take: itemsPerPage,
    ...filter,
  });
};

export const SettingsCentersContext = createContext<{
  workshops: Option[];
  branchOffices: Option[];
}>({
  workshops: [],
  branchOffices: [],
});

const SettingsCentersPage: FC = () => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [countOfCenters, setCountOfCenters] = useState(0);
  const [centers, setCenters] = useState<CenterDto[]>([]);
  const { showToast, showGenericErrorToast } = useShowToast();

  const [chosenCenter, setChosenCenter] = useState<CenterDto | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState<CenterFilter>({});

  const [workshops, setWorkshops] = useState<Option[]>([]);
  const [branchOffices, setBranchOffices] = useState<Option[]>([]);
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE);

  const centersColumnsDef: ColDef<CenterDto>[] = [
    { key: 'name', nested: false, isDate: false, sortable: true },
    { key: 'branchOffice', nested: true, isDate: false, sortable: true },
    { key: 'workshop', nested: true, isDate: false, sortable: true },
    { key: 'comment', nested: false, isDate: false, sortable: false },
  ];

  const fetchData = useCallback(async () => {
    try {
      const filteredData = await fetchCenters(currentPage, rowsPerPage, filter);
      setCenters(filteredData.data as CenterDto[]);
      setCountOfCenters(filteredData.metadata?.total || 0);
    } catch (e) {
      showGenericErrorToast();
    }
  }, [currentPage, filter, rowsPerPage, showGenericErrorToast]);

  useEffect(() => {
    Promise.all([fetchWorkShops(), fetchWorkBranchOffices()]).then(
      ([workshops, branchOffices]) => {
        const workshopsOptions = (workshops || []).map(
          (workshop: WorkshopDto) => ({
            value: workshop.id || 0,
            label: workshop.name || '',
          })
        );

        const branchOfficesOptions = branchOffices.map(
          (branchOffice: BranchOfficeDto) => ({
            value: branchOffice.id || 0,
            label: branchOffice.name || '',
          })
        );

        setWorkshops(workshopsOptions);

        setBranchOffices(branchOfficesOptions);
      }
    );
  }, [t]);

  useEffect(() => {
    fetchData();
  }, [currentPage, fetchData, filter, rowsPerPage]);

  const handleAddCenter = () => {
    setChosenCenter(null);
    setShowModal(true);
  };

  const handleEditCenter = (center: CenterDto) => {
    if (!center) return;
    setChosenCenter(center);
    setShowModal(true);
  };

  const handleChangePage = (page: number) => {
    setCurrentPage(page);
  };

  const handleSubmit = async (
    values: SettingsCenterFormOutputValues,
    id?: number
  ) => {
    const valuesWithoutNullFields = nullFieldsToUndefined(values);

    if (id) {
      try {
        const data = (
          await CenterService.putCenterUpdate({
            id,
            requestBody: valuesWithoutNullFields as CenterRequestDto,
          })
        ).data;

        if (data) {
          setCenters(
            (centers) => updateObjectInArray(centers, data) as CenterDto[]
          );
        }

        setChosenCenter(null);
        setShowModal(false);

        showToast('success', t('notifications.itemWasSuccessfullyEdited'));
      } catch (e) {
        showGenericErrorToast();
      }
    } else {
      try {
        await CenterService.postCenterCreate({
          requestBody: valuesWithoutNullFields as CenterRequestDto,
        });

        fetchData();

        setShowModal(false);
        showToast('success', t('notifications.itemWasSuccessfullyCreated'));
      } catch (e) {
        showGenericErrorToast();
      }
    }
  };

  const handleCancel = () => {
    setChosenCenter(null);
    setShowModal(false);
  };

  const handleDeleteCenter = async (
    id: number,
    setShowModal: Dispatch<SetStateAction<boolean>>
  ) => {
    if (!id) return;

    try {
      await CenterService.deleteCenterDelete({ id });

      showToast('success', t('notifications.itemWasSuccessfullyDeleted'));

      if (centers.length === 1 && currentPage !== 1) {
        setCurrentPage((page) => page - 1);
      }

      fetchData();
      setShowModal(false);
    } catch (e) {
      showGenericErrorToast();
    }
  };

  const updateFilter = (key: string, value: string) => {
    setFilter((filter) => updateFilterData(filter, { key, value }));
  };

  return (
    <SettingsCentersContext.Provider value={{ workshops, branchOffices }}>
      <CDataGrid
        colDef={centersColumnsDef}
        data={centers}
        translationKey="centersSettingsPage"
        onAdd={handleAddCenter}
        onDelete={handleDeleteCenter}
        onEdit={(values) => handleEditCenter(values as CenterDto)}
        filters={<CentersFilters updateFilter={updateFilter} filter={filter} />}
        changeRowsPerPage={(rowsPerPage: number) => setRowsPerPage(rowsPerPage)}
        onSort={(sortModel) => {
          setFilter((filter) => ({
            ...filter,
            sortBy: sortModel.sortBy,
            descending: sortModel.descending,
          }));
        }}
        pagination={{
          onPageChange: handleChangePage,
          totalCount: countOfCenters,
          currentPage,
          pageSize: rowsPerPage,
        }}
      />
      <Modal
        open={showModal}
        title={t('navigation.centers')}
        onClose={() => setShowModal(false)}
        type={chosenCenter ? 'edit' : 'create'}
      >
        <SettingsCenterForm
          center={chosenCenter}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
        />
      </Modal>
    </SettingsCentersContext.Provider>
  );
};
export default SettingsCentersPage;
